import moment from "moment";
import { Button } from "@/components/ui/button";
import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { property_classes, property_structures, property_types } from "@/models/properties.model";
import { PropertyWithRelations } from "@/repositories/properties.repository";
import { updatePropertyDetail } from "../properties-detail.slice";
import { useAppDispatch } from "@/lib/store";
import { capitalize } from "@/lib/utils";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { BILL_TO_ADDRESS_MAX_LENGTH, BILL_TO_NAME_MAX_LENGTH } from "@shyne/shared/api";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  year_built: z.coerce.number().min(1800, "Must be >= 1800").max(moment().year(), "Future year?"),
  floors: z.coerce.number().min(1),
  type: z.array(z.nativeEnum(property_types)).min(1, "At least one type is required"),
  structure: z.array(z.nativeEnum(property_structures)).min(1, "At least one structure is required"),
  class: z.array(z.nativeEnum(property_classes)).min(1, "At least one class is required"),
  image: z.union([z.instanceof(File), z.string()]).optional(),
  bill_to_name: z.string().max(BILL_TO_NAME_MAX_LENGTH).optional(),
  bill_to_address: z.string().max(BILL_TO_ADDRESS_MAX_LENGTH).optional(),
});

interface Props {
  property: PropertyWithRelations;
}

export function UpdatePropertyModal({ property }: Props) {
  const dispatch = useAppDispatch();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: property.property.name,
      description: property.property.description,
      year_built: property.property.year_built,
      floors: property.property.floors,
      type: property.property.type ?? [],
      structure: property.property.structure ?? [],
      class: property.property.class ?? [],
      image: property.property.image ?? "",
      bill_to_name: property.property.bill_to_name ?? "",
      bill_to_address: property.property.bill_to_address ?? "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    await dispatch(updatePropertyDetail({ id: property.property.id, updates: values })).unwrap();
  }

  const formDefinition: FormFieldDef[] = [
    {
      name: "image",
      label: "Property Image",
      type: "file",
      //   required: true,
    },
    {
      type: "text",
      name: "name",
      label: "Name",
      required: true,
    },
    {
      type: "textarea",
      name: "description",
      label: "Description",
    },
    {
      type: "number",
      name: "year_built",
      label: "Year Built",
      required: true,
    },
    {
      type: "number",
      name: "floors",
      label: "Floors",
      required: true,
    },
    {
      type: "select",
      name: "type",
      label: "Type",
      required: true,
      multiple: true,
      options: Object.entries(property_types).map(([label, value]) => ({
        label: capitalize(label),
        value,
      })),
    },
    {
      type: "select",
      name: "structure",
      label: "Structure",
      required: true,
      multiple: true,
      options: Object.entries(property_structures).map(([label, value]) => ({
        label: capitalize(label),
        value,
      })),
    },
    {
      type: "select",
      name: "class",
      label: "Class",
      required: true,
      multiple: true,
      options: Object.entries(property_classes).map(([label, value]) => ({
        label: capitalize(label),
        value,
      })),
    },
    {
      type: "text",
      name: "bill_to_name",
      label: "Bill to name",
      placeholder: "The customer's name when left empty",
    },
    {
      type: "textarea",
      name: "bill_to_address",
      label: "Bill to address",
    },
  ];

  const formCreatorProps: FormCreatorProps = {
    form,
    onSubmit,
    formDefinition,
    buttonText: "Update Property",
  };

  return (
    <Sheet>
      <SheetTrigger className="w-full">
        <Button variant="outline" className="w-full">
          Edit Property
        </Button>
      </SheetTrigger>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Property</SheetTitle>
          <SheetDescription>
            <FormCreator {...formCreatorProps} />
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}
